/**
 * When has this visitor PAUSED?
 *
 * The brief asks the concierge to reach out at intentional pauses in the other
 * journeys on the site — not on a timer, and not the moment a page loads. The
 * difference matters: a panel that opens while someone is still reading is an
 * interruption, and the thing they remember about it is that they closed it.
 *
 * So a pause is a positive signal that someone has STOPPED, and there are three
 * honest ones:
 *
 *   · **Dwell** — the page has been still for a while. No scrolling, no
 *     clicking, no typing. They are reading, or they have stalled.
 *   · **Finished** — they scrolled to the bottom of the page. There is nothing
 *     left to read, which is the most natural moment on any page to be asked
 *     something.
 *   · **Leaving** — the pointer has left through the top of the window, toward
 *     the tab bar or the back button. Desktop only; there is no honest mobile
 *     equivalent, and faking one with a scroll-up heuristic fires on people who
 *     are simply re-reading.
 *
 * WHO IT FIRES FOR
 * First-time visitors and visitors with no account — the people the widget
 * exists for. Someone signed in has other ways to reach us and a stronger claim
 * to be left alone.
 *
 * ONCE. EVER.
 * The decision is persisted, so the nudge does not reappear on the next page,
 * the next tab, or tomorrow. A nudge that comes back is not a nudge.
 */

import { useEffect, useRef, useState } from 'react';

const NUDGED_KEY = 'iv_concierge_nudged';

/** Stillness before a page counts as dwelt-on. Long enough to be reading. */
const DWELL_MS = 28_000;

/** Grace period after arriving, so a nudge never lands on top of a page load. */
const SETTLE_MS = 6_000;

/** How close to the bottom counts as having finished the page. */
const BOTTOM_SLACK_PX = 120;

export type PauseReason = 'dwell' | 'finished' | 'leaving';

export function hasBeenNudged(): boolean {
  try {
    return window.localStorage.getItem(NUDGED_KEY) === '1';
  } catch {
    // Storage blocked. Treat as not-yet-nudged; the in-memory guard below still
    // stops it firing twice in one visit, which is the case that actually annoys.
    return false;
  }
}

export function markNudged(): void {
  try {
    window.localStorage.setItem(NUDGED_KEY, '1');
  } catch {
    // Nothing to do — the session guard carries it for this visit.
  }
}

interface JourneyPauseOptions {
  /** Off entirely — signed in, already chatting, or on a route that owns focus. */
  enabled: boolean;
}

/**
 * Returns the reason the visitor paused, or null. Fires at most once.
 */
export function useJourneyPause({ enabled }: JourneyPauseOptions): PauseReason | null {
  const [reason, setReason] = useState<PauseReason | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || firedRef.current || hasBeenNudged()) {
      return;
    }

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    const fire = (why: PauseReason): void => {
      if (firedRef.current || !settled) {
        return;
      }
      firedRef.current = true;
      markNudged();
      setReason(why);
    };

    const restartDwell = (): void => {
      if (dwellTimer) {
        clearTimeout(dwellTimer);
      }
      dwellTimer = setTimeout(() => fire('dwell'), DWELL_MS);
    };

    const onScroll = (): void => {
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - BOTTOM_SLACK_PX;
      if (atBottom) {
        // Only meaningful on a page long enough to have a bottom worth reaching.
        if (document.documentElement.scrollHeight > window.innerHeight * 1.4) {
          fire('finished');
          return;
        }
      }
      restartDwell();
    };

    const onMouseOut = (event: MouseEvent): void => {
      // relatedTarget is null when the pointer leaves the window entirely.
      if (!event.relatedTarget && event.clientY <= 0) {
        fire('leaving');
      }
    };

    const settleTimer = setTimeout(() => {
      settled = true;
      restartDwell();
    }, SETTLE_MS);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointermove', restartDwell, { passive: true });
    window.addEventListener('keydown', restartDwell);
    document.addEventListener('mouseout', onMouseOut);

    return () => {
      clearTimeout(settleTimer);
      if (dwellTimer) {
        clearTimeout(dwellTimer);
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointermove', restartDwell);
      window.removeEventListener('keydown', restartDwell);
      document.removeEventListener('mouseout', onMouseOut);
    };
  }, [enabled]);

  return reason;
}
